import { environments } from '../config';

/**
 * Determines the correct Heap application ID based on the environment and hostname.
 *
 * @function getHeapAppId
 * @returns {string} The Heap application ID.
 */
const getHeapAppId = () => {
    const env = process.env.NODE_ENV;
    const hostname = window.location.hostname;

    if (env === 'development') {
        return environments.development.HEAP_APP_ID;
    } else if (env === 'production') {
        const stages = Object.keys(environments.production);

        for (const key of stages) {
            const stageConfig = environments.production[key];
            const subDomains = stageConfig.SUBDOMAIN.split(',');
            const domains = stageConfig.DOMAIN.split(',');

            for (const domain of domains) {
                for (const subDomain of subDomains) {
                    if (
                        hostname === `${subDomain}.${domain}` ||
                        hostname === domain
                    ) {
                        return stageConfig.HEAP_APP_ID;
                    }
                }
            }
        }
        // If there are no matches, default to the test environment HEAP_APP_ID
        return environments.production.test.HEAP_APP_ID;
    }
    // Fallback for other NODE_ENV values, like 'test'
    return environments.production.test.HEAP_APP_ID;
};

/**
 * Dynamically injects the Heap Analytics script into the HTML document.
 *
 * The function determines the appropriate Heap ID based on the environment.
 * It uses the development Heap ID if the `NODE_ENV` is not 'production', or
 * if the hostname includes 'test' or 'staging'. Otherwise, it uses the
 * production Heap ID.
 *
 * Before injecting, it checks if the Heap script is already loaded to avoid
 * duplicating the script.
 *
 * The function then creates a script element with the Heap Analytics code
 * and appends it to the document head.
 *
 * @function injectHeapScript
 * @returns {void}
 */
export const injectHeapScript = () => {
    const heapId = getHeapAppId();

    // Check if the Heap script is already loaded
    if (
        document.querySelector(
            `script[src="https://cdn.heapanalytics.com/js/heap-${heapId}.js"]`
        )
    ) {
        return;
    }

    const scriptContent = `
    window.heap = window.heap || [];
    heap.load = function(e, t) {
      window.heap.appid = e;
      window.heap.config = t = t || {};
      var r = document.createElement('script');
      r.type = 'text/javascript';
      r.async = true;
      r.src = 'https://cdn.heapanalytics.com/js/heap-' + e + '.js';
      var a = document.getElementsByTagName('script')[0];
      a.parentNode.insertBefore(r, a);
      var n = function(e) {
        return function() {
          heap.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      var p = ['addEventProperties', 'addUserProperties', 'clearEventProperties', 'identify', 'resetIdentity', 'removeEventProperty', 'setEventProperties', 'track', 'unsetEventProperty'];
      for (var o = 0; o < p.length; o++) heap[p[o]] = n(p[o]);
    };
    heap.load('${heapId}');
  `;

    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.text = scriptContent;
    document.head.appendChild(scriptElement);
};
